import { addDays, format, isValid, parseISO } from "date-fns";

const hasWindowValue = (value) =>
  value !== undefined && value !== null && value !== "";

const hasOwn = (subject, key) =>
  Object.prototype.hasOwnProperty.call(subject || {}, key);

const isSelectableGroupSource = (source) => {
  if (!source?.is_delivered || source?.delivery_date_expired) {
    return false;
  }

  const remainingQuantity =
    source?.remaining_qty !== undefined && source?.remaining_qty !== null
      ? Number(source.remaining_qty)
      : Number(source?.quantity || 0);

  return Number.isFinite(remainingQuantity) && remainingQuantity > 0;
};

const parseWindowDate = (value) => {
  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }

  if (!hasWindowValue(value)) {
    return null;
  }

  const parsed = parseISO(String(value));
  return isValid(parsed) ? parsed : null;
};

const positiveDays = (value) => {
  const days = Number(value);
  return Number.isFinite(days) && days > 0 ? days : null;
};

const emptyWindows = (source = null) => ({
  returnDate: null,
  exchangeDate: null,
  sameDate: false,
  source,
});

export const resolveRequestWindows = ({
  subject = {},
  rmsConfig = {},
  deliveredAt = subject?.delivered_at,
  eligible = subject?.eligible,
  forceExchange = false,
} = {}) => {
  if (eligible === false || !deliveredAt) {
    return emptyWindows();
  }

  const supportsReturn =
    rmsConfig?.supports_refund !== false &&
    subject?.refund_eligible !== false &&
    !subject?.return_date_expired;
  const supportsExchange =
    Boolean(rmsConfig?.supports_exchange || forceExchange) &&
    subject?.exchange_eligible !== false &&
    !subject?.exchange_date_expired;

  const hasWindowContract =
    hasOwn(subject, "return_window_open_till") ||
    hasOwn(subject, "exchange_window_open_till");

  let returnDate = null;
  let exchangeDate = null;

  if (hasWindowContract) {
    returnDate = supportsReturn
      ? parseWindowDate(subject.return_window_open_till)
      : null;
    exchangeDate = supportsExchange
      ? parseWindowDate(subject.exchange_window_open_till)
      : null;
  } else {
    const deliveredDate = parseWindowDate(deliveredAt);
    if (!deliveredDate) {
      return emptyWindows();
    }

    const returnDays = positiveDays(
      rmsConfig?.days_to_raise_request_threshold,
    );
    const exchangeDays =
      positiveDays(rmsConfig?.days_to_raise_request_threshold_exchange) ||
      returnDays;

    returnDate =
      supportsReturn && returnDays ? addDays(deliveredDate, returnDays) : null;
    exchangeDate =
      supportsExchange && exchangeDays
        ? addDays(deliveredDate, exchangeDays)
        : null;
  }

  const sameDate = Boolean(
    returnDate &&
      exchangeDate &&
      format(returnDate, "yyyy-MM-dd") ===
        format(exchangeDate, "yyyy-MM-dd"),
  );

  return {
    returnDate,
    exchangeDate,
    sameDate,
    source: hasWindowContract ? "orders" : "rmsConfig",
  };
};

export const getLatestRequestWindow = (
  items = [],
  windowKey,
  eligibilityKey,
) => {
  const latest = items.reduce((currentLatest, item) => {
    if (
      item?.[eligibilityKey] === false ||
      item?.[
        windowKey === "return_window_open_till"
          ? "return_date_expired"
          : "exchange_date_expired"
      ]
    ) {
      return currentLatest;
    }

    const candidate = parseWindowDate(item?.[windowKey]);
    if (!candidate || (currentLatest && candidate <= currentLatest)) {
      return currentLatest;
    }

    return candidate;
  }, null);

  return latest ? format(latest, "yyyy-MM-dd") : null;
};

export const getAggregatedRequestWindows = (parent = {}, children = []) => {
  const sources = [parent, ...children].filter(isSelectableGroupSource);
  const hasWindowContract = sources.some(
    (source) =>
      hasOwn(source, "return_window_open_till") ||
      hasOwn(source, "exchange_window_open_till"),
  );

  if (!hasWindowContract) {
    return {};
  }

  return {
    return_window_open_till: getLatestRequestWindow(
      sources,
      "return_window_open_till",
      "refund_eligible",
    ),
    exchange_window_open_till: getLatestRequestWindow(
      sources,
      "exchange_window_open_till",
      "exchange_eligible",
    ),
  };
};

export const formatRequestWindow = (date, pattern = "dd MMM yyyy") =>
  date ? format(date, pattern) : "";
